const JUDAICA_CATEGORIES = [
  {
    key: 'mezuzah_cases',
    href: '/s/judaica/mezuzas',
    title: 'Mezuzah Cases for the Doorway',
    description:
      'Ceramic, wood, Jerusalem stone, and hand-painted designs — suited for different doorways and styles.',
  },
  {
    key: 'name_plates',
    href: '/s/judaica/nameplates',
    title: 'Personalized Name Plates for the Home',
    description:
      'Custom-made signs in Hebrew, English, or both — designed for a specific home or family.',
  },
  {
    key: 'shabbat_gifts',
    href: '/s/judaica/shabbos',
    title: 'Shabbat Table Pieces',
    description: 'Challah boards, covers, and candle holders that shape the table each week.',
  },
  {
    key: 'milestones',
    href: '/s/judaica',
    title: 'Jewish Gifts for Weddings & Milestones',
    description:
      'Common choices for new homes, weddings, and life events — often personalized or made to order.',
  },
  {
    key: 'holidays',
    href: '/s/judaica',
    title: 'Judaica for Jewish Holidays',
    description: 'Seasonal pieces tied to the calendar, from Chanukah to Rosh Hashanah.',
  },
  {
    key: 'home_decor',
    href: '/s/judaica/decor',
    title: 'Judaica for the Home',
    description:
      'Everyday pieces like Birkat Habayit and Asher Yatzar that settle into a home over time.',
  },
];

const getSchema = rootURL => {
  const judaicaURL = `${rootURL}/s/judaica`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${rootURL}/s/judaica#webpage`,
      url: judaicaURL,
      name: 'Handmade Judaica & Jewish Gifts — From Independent Jewish Artists',
      description:
        'Handmade Judaica and Jewish home decor — mezuzah cases, name plates, Shabbat and yom tov pieces — made and sold directly by independent Jewish artists.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Handmade Judaica' },
        { '@type': 'Thing', name: 'Jewish Gifts' },
        { '@type': 'Thing', name: 'Mezuzah Cases' },
        { '@type': 'Thing', name: 'Jewish Home Decor' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      mainEntity: { '@id': `${rootURL}/s/judaica#categories` },
    },
    {
      '@type': 'ItemList',
      '@id': `${rootURL}/s/judaica#categories`,
      name: 'Judaica Gift Categories',
      itemListElement: JUDAICA_CATEGORIES.map((cat, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: cat.title,
        url: `${rootURL}${cat.href}`,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${rootURL}/s/judaica#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What are the most common Judaica gifts?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The most common starting points are mezuzah cases, Shabbat table pieces like challah boards and candle holders, and personalized items such as family name plates. These fit naturally into a Jewish home and work across a wide range of occasions.',
          },
        },
        {
          '@type': 'Question',
          name: 'What types of Judaica are typically used in the home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Judaica used in the home usually falls into a few categories: doorway items like mezuzah cases, Shabbat table pieces, holiday-specific items, and decorative pieces such as Birkat Habayit or Asher Yatzar. Most homes build this up gradually over time.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I buy as a Jewish gift for a home or occasion?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For a new home, mezuzah cases and name plates are common choices. For Shabbat or general use, challah boards or candle holders are typical. For weddings or milestones, many people choose something personalized or commission a custom piece.',
          },
        },
        {
          '@type': 'Question',
          name: 'What makes a Judaica gift meaningful or appropriate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Judaica gifts are both decorative and functional. Items that are used regularly — like mezuzah cases or Shabbat table pieces — tend to feel more meaningful because they become part of daily or weekly life. For many people, the right choice depends on the occasion and the home, not just the item itself.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can Judaica gifts be personalized or custom-made?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many pieces can be personalized, especially name plates and home decor items. Some artists also offer custom work, where you can request a specific size, design, or text. This is often used for weddings, new homes, or milestone gifts.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I choose the right Judaica gift?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "A simple way to decide: Start with the occasion — home, Shabbat, or milestone. Choose a type of item that fits that use. Then consider style and personalization. If you're unsure, starting with something familiar — like a mezuzah case or Shabbat piece — usually works well.",
          },
        },
      ],
    },
  ];
};

const judaicaSeoConfig = {
  key: 'judaica',
  match: ({ listingType, activeJudaicaCollection }) =>
    listingType === 'judaica' && !activeJudaicaCollection,
  accentColor: '--colorJudaica',
  links: JUDAICA_CATEGORIES,
  title: 'Handmade Judaica & Jewish Gifts — From Independent Jewish Artists',
  description:
    'Handmade Judaica and Jewish home decor — mezuzah cases, name plates, Shabbat and yom tov pieces — made and sold directly by independent Jewish artists.',
  getSchema,
  intro: {
    heading: 'Handmade Judaica & Jewish Gifts',
    tagline:
      'Handmade Judaica and Jewish home decor — mezuzah cases, name plates, Shabbat and yom tov pieces — made and sold directly by independent Jewish artists.',
    collapsedHeading: 'Judaica & Jewish Gifts',
    bodyParagraphs: [
      'Joodaic is a marketplace for Judaica that comes straight from the artist\'s studio. The catalog spans traditional, familiar pieces alongside more contemporary work, across the full range of Jewish homes.',
      'Most people come here looking for a gift or something for a home, not a specific item. The most common starting points are mezuzah cases for the doorway, Shabbat table pieces, and personalized items like name plates — depending on the space and the occasion.',
    ],
    linksLabel: 'Browse Judaica by Category',
  },
  bottomSections: [
    {
      id: 'intro',
      type: 'prose',
      heading: "What you'll find here",
      paragraphs: [
        'You can also browse our <a href="/s/art">Jewish art collection</a>.',
        'Mezuzah cases are the largest category — in porcelain, olive wood, Jerusalem stone, ceramic, hand-painted, resin, and more modern minimalist styles. For personalized pieces at the entrance, browse <a href="/s/judaica/nameplates">Jewish door name plates</a> made to order by independent Jewish artists.',
        'Shabbat pieces follow — challah boards, challah covers, and candle holders that shape the table each week. Yom tov work appears throughout the year, from menorahs before Chanukah to Rosh Hashanah table pieces.',
        'Birkat Habayit, Asher Yatzar, and other decorative pieces round out the everyday items people add to a Jewish home over time.',
      ],
    },
    {
      id: 'choosing',
      title: 'Choosing Judaica',
      paragraphs: [
        "For some people, the first mezuzah case in a new home is a placeholder. For others, it's something they choose carefully from the start.",
      ],
      subheadings: [
        {
          title: 'Most Judaica gifts fall into a few broad categories:',
          list: [
            'Doorway items — mezuzah cases, often chosen for a new home',
            'Shabbat table pieces — challah boards, covers, and candle holders used weekly',
            'Personalized items — name plates and home pieces made for a specific family',
            'Holiday pieces — items tied to specific times of year',
          ],
        },
        {
          title: 'A simple way to approach it:',
          list: [
            'For a new home — mezuzah cases or name plates',
            'For Shabbat use — challah boards, covers, or candle holders',
            'For a gift — something personalized or tied to the occasion',
          ],
        },
      ],
      paragraphsAfter: [
        'Starting with the occasion and choosing the type of item that fits usually makes the decision easier.',
      ],
    },
    {
      id: 'small_runs',
      title: 'Made in small runs, sold directly',
      paragraphs: [
        'Most pieces on Joodaic are made by hand in small runs and sold directly by the artist. A mezuzah case might be one of a limited batch in that finish, and a candle holder is often hand-thrown or hand-cast and finished individually.',
        'Pricing reflects materials and labor rather than retail markup.',
      ],
    },
    {
      id: 'custom',
      title: 'Custom and personalized work',
      paragraphs: [
        'A large part of the catalog can be customized — often for a specific home, name, or occasion.',
        'Family name plates can be made in Hebrew, English, or both. Many mezuzah cases come in size variants and finish options. Birkat Habayit and Asher Yatzar pieces can often be personalized with names, dates, or text.',
        'To start a custom order, you can message the artist directly through their storefront. Some listings also allow you to send personalization details at checkout.',
      ],
    },
    {
      id: 'artists',
      title: 'About the artists',
      paragraphs: [
        "Every artist on Joodaic is an independent Jewish maker. Each storefront is theirs to run — Joodaic is the platform, not the studio.",
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'What are the most common Judaica gifts?',
      answer: [
        'The most common starting points are mezuzah cases, Shabbat table pieces like challah boards and candle holders, and personalized items such as family name plates. These fit naturally into a Jewish home and work across a wide range of occasions.',
      ],
    },
    {
      question: 'What types of Judaica are typically used in the home?',
      answer: [
        'Judaica used in the home usually falls into a few categories: doorway items like mezuzah cases, Shabbat table pieces, holiday-specific items, and decorative pieces such as Birkat Habayit or Asher Yatzar.',
        'Most homes build this up gradually over time.',
      ],
    },
    {
      question: 'What should I buy as a Jewish gift for a home or occasion?',
      answer: [
        'For a new home, mezuzah cases and name plates are common choices. For Shabbat or general use, challah boards or candle holders are typical. For weddings or milestones, many people choose something personalized or commission a custom piece.',
      ],
    },
    {
      question: 'What makes a Judaica gift meaningful or appropriate?',
      answer: [
        'Most Judaica gifts are both decorative and functional. Items that are used regularly — like mezuzah cases or Shabbat table pieces — tend to feel more meaningful because they become part of daily or weekly life.',
        'For many people, the right choice depends on the occasion and the home, not just the item itself.',
      ],
    },
    {
      question: 'Can Judaica gifts be personalized or custom-made?',
      answer: [
        'Many pieces can be personalized, especially name plates and home decor items. Some artists also offer custom work, where you can request a specific size, design, or text.',
        'This is often used for weddings, new homes, or milestone gifts.',
      ],
    },
    {
      question: 'How do I choose the right Judaica gift?',
      answer: [
        'A simple way to decide:',
        [
          'Start with the occasion — home, Shabbat, or milestone',
          'Choose a type of item that fits that use',
          'Then consider style and personalization',
        ],
        "If you're unsure, starting with something familiar — like a mezuzah case or Shabbat piece — usually works well.",
      ],
    },
  ],
};

export default judaicaSeoConfig;
